import Button from "components/buttons";
import Divider from "components/divider";
import DynamicFormInput from "components/form/form";
import FileUpload from "components/FileUpload";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useRef, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

import {
  createLegalResearchRequest,
  updateLegalResearchRequest,
} from "services/legalResearchService";
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  TokenPayload,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  createLegislativeDrafting,
  updateLegislativeDrafting,
} from "services/legislativeService";
import {
  createLegalAdvisory,
  updateLegalAdvisory,
} from "services/legalAdvisoryService";
import {
  createSectorDirective,
  updateSectorDirective,
} from "services/sectorDirectives";
import { jwtDecode } from "jwt-decode";

const CreateSectorDirectiveRequest = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();

  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    explanatory_notes: editData?.explanatory_notes || "",
    request_letter_reference_number:
      editData?.request_letter_reference_number || "",
    request_letter_files: null as File | null,
    directive_files: null as File | null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefDraftInstruction = useRef<HTMLInputElement>(null);

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: any,
    fieldName: keyof Pick<
      typeof formState,
      "request_letter_files" | "directive_files"
    >
  ) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: file,
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.request_letter_reference_number)
      newErrors.request_letter_reference_number = t('directives.letter_ref_number_required');
    if (!formState.explanatory_notes)
      newErrors.explanatory_notes = t('directives.explanatory_notes_required');
    if (!formState.request_letter_files)
      newErrors.request_letter_files = t('directives.letter_required');
    if (!formState.directive_files)
      newErrors.directive_files = t('directives.file_required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast(t('directives.fill_all_fields'), 'error');
      return;
    }
    setSubmitting(true);

    const data = new FormData();
    for (const key in formState) {
      const value = formState[key as keyof typeof formState];
      if (value !== null) {
        if (value instanceof File) {
          data.append(key, value);
        } else {
          data.append(key, String(value));
        }
      }
    }
    data.append("status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.submitted.value);
    if (editData) {
      try {
        const response = await updateSectorDirective(editData.id, data, contextData.token || contextData?.user?.access_token || "");
        if (response.id) {
          navigate(`/sector-directives/${response.id}`); // Path is not localized
        }
        showToast(t('directives.request_success'), 'success');
        if (fileInputRefRequestLetter.current)
          fileInputRefRequestLetter.current.value = "";
        if (fileInputRefDraftInstruction.current)
          fileInputRefDraftInstruction.current.value = "";
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t('FailedToSendSectorDirectiveRequest'), "error");
        showToast(t('directives.request_failed'), 'error');
      } finally {
        setSubmitting(false);
      }
    } else {
      const decoded = jwtDecode<TokenPayload>(
        contextData.user.access_token || ""
      );
      data.append(
        "requested_by",
        contextData.user?.user_type === "external"
          ? "stakeholder"
          : contextData.user?.user_type
      );
      data.append("requested_by_id", contextData.user?.uu_id ?? "");
      data.append("first_name", decoded?.first_name || "");
      data.append("last_name", decoded?.last_name || "");
      data.append("email_user_organization", decoded?.email || "");
      data.append(
        "organization_kebele",
        decoded?.organization?.address?.kebele || ""
      );
      data.append(
        "organization_zone",
        decoded?.organization?.address?.zone || ""
      );
      data.append(
        "organization_state",
        decoded?.organization?.address?.state || ""
      );
      data.append("organization_name", decoded?.organization?.name || "");
      data.append("organization_type", decoded?.organization?.type || "");
      data.append("stakeholder_id", contextData.user?.org_unit_id ?? "");
       try {
        const response = await createSectorDirective(data, contextData.token || contextData?.user?.access_token || "");
        if (response.id) {
          navigate(`/sector-directives/${response.id}`); // Path is not localized
        }
        showToast(t('directives.request_success'), 'success');
        setErrors({});
        if (fileInputRefRequestLetter.current)
          fileInputRefRequestLetter.current.value = "";
        if (fileInputRefDraftInstruction.current)
          fileInputRefDraftInstruction.current.value = "";
      } catch (error) {
        console.error("Error submitting request:", error);
        showToast(t('directives.request_failed'), 'error');
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div>
      <PageTitle
        icon="⚖" // Icon is not text, no localization needed
        text={t('directives.request_title')}
        subtitle={t('directives.submit_request_desc_alt')}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl">
              <div className="flex flex-col md:flex-row gap-6 mt-4">
                <div className="flex-1">
                  <FileUpload
                    label={t('RequestLetter')} // Label should be RequestLetter, error message is separate
                    onChange={(file) =>
                      handleFileChange(file, "request_letter_files")
                    }
                    ref={fileInputRefRequestLetter}
                  />
                  {errors.request_letter_files && (
                    <span className="text-red-500 text-sm">
                      {t(errors.request_letter_files)}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <FileUpload
                    label={t('DirectiveFile')} // Label should be DirectiveFile, error message is separate
                    onChange={(file) =>
                      handleFileChange(file, "directive_files")
                    }
                    ref={fileInputRefDraftInstruction}
                  />
                  {errors.directive_files && (
                    <span className="text-red-500 text-sm">
                      {t(errors.directive_files)}
                    </span>
                  )}
                </div>
              </div>
              <DynamicFormInput
                inputs={[
                  {
                    value: formState.request_letter_reference_number,
                    type: "number",
                    label: t('directives.letter_ref_number_label'),
                    name: "request_letter_reference_number",
                    onChange: handleChange,
                    placeholder: t('directives.letter_ref_number_placeholder'),
                  },
                ]}
              />
              {errors.request_letter_reference_number && (
                <span className="text-red-500 text-sm">
                  {t(errors.request_letter_reference_number)}
                </span>
              )}

              <DynamicFormInput
                inputs={[
                  {
                    value: formState.explanatory_notes,
                    type: "textarea",
                    label: t('directives.explanatory_notes_label'),
                    name: "explanatory_notes",
                    onChange: handleChange,
                    placeholder: t('DetailHere'), // Already localized
                  },
                ]}
              />
              {errors.explanatory_notes && (
                <span className="text-red-500 text-sm">
                  {t(errors.explanatory_notes)}
                </span>
              )}

              <br />
              <div className="flex space-x-4 justify-between">
                <Button
                  variant="secondary"
                  onClick={() => {
                    if (fileInputRefRequestLetter.current)
                      fileInputRefRequestLetter.current.value = "";
                    if (fileInputRefDraftInstruction.current)
                      fileInputRefDraftInstruction.current.value = "";
                    navigate(-1);
                  }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={() => console.log()}
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t('Submitting')}...
                    </div>
                  ) : (
                    t('directives.send_request')
                  )}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes // Notes component itself might need internal localization for title/text props
                title={t('Note')} // Already localized
                text={t('directives_notes.note_text')}
                subtext={t('directives_notes.note_subtext')}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold"> 
                      {t('directives_notes.requirements_title')}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {t('directives_notes.requirements_desc')}
                    </p>
                  </div>
                </div>
                <div className="border-t pt-2 space-y-2">
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">{t('RequestLetter')}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                    <span className="text-sm font-medium">{t('DirectiveFile')}</span>
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </form>
    </div>
  );
};

export default CreateSectorDirectiveRequest;